import React from "react";
import useSWR from "swr";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Download, CheckCircle2, Clock, PenLine } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const fetcher = (u) => api.get(u).then((r) => r.data);
const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  draft:     { c: "bg-slate-100 text-slate-700", i: <Clock className="w-3 h-3 mr-1" />, l: "Draft" },
  pending:   { c: "bg-amber-100 text-amber-800", i: <Clock className="w-3 h-3 mr-1" />, l: "Awaiting your signature" },
  signed:    { c: "bg-emerald-100 text-emerald-800", i: <CheckCircle2 className="w-3 h-3 mr-1" />, l: "Signed" },
  cancelled: { c: "bg-red-100 text-red-800", i: null, l: "Cancelled" },
};

const DriverContracts = () => {
  const { data: contracts = [] } = useSWR("/driver-portal/contracts", fetcher);
  const nav = useNavigate();

  const startSigning = async (id) => {
    try {
      const { data } = await api.post(`/driver-portal/contracts/${id}/self-sign-token`);
      nav(data.signing_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start signing");
    }
  };

  return (
    <div className="p-4 pt-6 space-y-4 pb-24 max-w-2xl mx-auto">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">My Contracts</div>
        <h1 className="font-heading text-2xl font-bold tracking-tight mt-1">Documents & Agreements</h1>
        <p className="text-sm text-slate-500 mt-1">
          View, sign and download your contracts. Signed PDFs are locked and cryptographically hashed.
        </p>
      </div>

      {contracts.length === 0 && (
        <Card className="p-6 text-center text-sm text-slate-500 rounded-2xl border-brand-border">
          You have no contracts yet. Your manager will send them when ready.
        </Card>
      )}

      {contracts.map((c) => {
        const s = STATUS_STYLE[c.status] || STATUS_STYLE.draft;
        return (
          <Card key={c.id} className="p-4 rounded-2xl border-brand-border" data-testid={`drv-ct-${c.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-brand-navy/5 grid place-items-center">
                <FileText className="w-5 h-5 text-brand-navy" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-mono">{c.contract_ref}</div>
                <div className="font-heading font-bold text-base truncate">{c.title}</div>
                <div className="mt-1"><Badge className={`${s.c} border-0`}>{s.i}{s.l}</Badge></div>
                {c.signed_at && (
                  <div className="text-xs text-slate-500 mt-1">
                    Signed on {c.signed_at?.slice(0, 10)}
                  </div>
                )}
              </div>
              <Button size="sm" variant="outline"
                      onClick={() => window.open(`${API}/api/driver-portal/contracts/${c.id}/pdf`, "_blank", "noopener")}
                      data-testid={`drv-ct-pdf-${c.id}`}>
                <Download className="w-3.5 h-3.5 mr-1" /> PDF
              </Button>
            </div>
            {(c.status === "pending" || c.status === "draft") && (
              <Button onClick={() => startSigning(c.id)}
                       className="w-full mt-3 bg-brand-red hover:bg-brand-red-hover"
                       data-testid={`drv-ct-sign-${c.id}`}>
                <PenLine className="w-4 h-4 mr-1.5" /> Sign now
              </Button>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default DriverContracts;
